import React from 'react'
import Head, { PageHead } from '@fs/zion-head'
import { useTranslation } from 'react-i18next'
import overviewImage from '../../pages/Overview/assets/ogImage.jpeg'
import learnMoreImage from '../../pages/LearnMore/assets/ogImage.jpeg'

const attributes = {
  overview: {
    image: {
      url: overviewImage,
      width: 1145,
      height: 600,
    },
    structuredData: (
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": {
              "@type": "ListItem",
              "position": 1,
              "name": "Old Family Photos",
              "item": "https://www.familysearch.org/old-family-photos/"
            }
          }
          `}
      </script>
    ),
  },
  find: {},
  gallery: {
    image: {
      url: learnMoreImage,
      width: 1140,
      height: 600,
    },
  },
  learnMore: {
    image: {
      url: learnMoreImage,
      width: 1140,
      height: 600,
    },
    structuredData: (
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Old Family Photos",
                "item": "https://www.familysearch.org/old-family-photos/"
              }, {
                "@type": "ListItem",
                "position": 2,
                "name": "Memories",
                "item": "https://www.familysearch.org/memories/learn-more"
              }
            ]
          }
          `}
      </script>
    ),
  },
  people: {},
  restore: {},
  memory: {
    image: {
      width: 1140,
      height: 600,
    },
  },
}

const useMetaContent = (page, dynamicMetadata, containsAlbumUrl) => {
  const [t] = useTranslation()

  const content = attributes[page]

  // this will keep the keys and default text available so that it doesn't break the i18next-scanner
  switch (page) {
    case 'overview': {
      content.title = t('memories.overview.page-title', 'Share Old Family Photos in Online Albums ')
      content.description = t(
        'memories.overview.meta-description',
        'Share old family photos of your parents, siblings, grandparents, and more. Create free online photo albums to share with others.'
      )
      content.socialShareTitle = t('memories.overview.social.title', 'Do You Have Any Old Family Photos?')
      content.socialShareDescription = t(
        'memories.overview.social.description',
        'Upload Them To FamilySearch and Share With Family for Free'
      )
      break
    }
    case 'find': {
      content.title = t('memories.find.meta.title', 'Find Memories')
      content.description = t(
        'memories.find.meta.description',
        'Publish and preserve your family history photos for free, and always available, on FamilySearch.'
      )
      content.socialShareTitle = t('memories.find.meta.title', 'Find Memories')
      content.socialShareDescription = t(
        'memories.find.meta.description',
        'Publish and preserve your family history photos for free, and always available, on FamilySearch.'
      )
      break
    }
    case 'gallery': {
      let pageTitle = t('memories.gallery.meta.title', 'Find Free Photo Preservation Tools in the FamilySearch Gallery')
      let pageDescription = t(
        'memories.gallery.meta.description',
        'Upload, organize, and share your family memories in the FamilySearch Gallery. Preserve your photos, documents, and more for future generations.'
      )
      let socialShareTitle = t(
        'memories.gallery.meta.social-title',
        'Free Photo Preservation Tools in Memories Gallery'
      )
      let socialShareDescription = t(
        'memories.gallery.meta.social-description',
        'Upload, organize, and preserve your family memories for generations to come.'
      )

      if (containsAlbumUrl) {
        if (dynamicMetadata?.title) {
          pageTitle = `${dynamicMetadata.title} | ${t('memories.gallery.meta.album-title', 'Memories Album')}`
        } else {
          pageTitle = t('memories.gallery.meta.album-title', 'Memories Album')
        }
        pageDescription = t(
          'memories.gallery.meta.album-description',
          'Follow the link to view this album on FamilySearch.'
        )
        socialShareTitle = pageTitle
        socialShareDescription = pageDescription
      }

      content.title = pageTitle
      content.description = pageDescription
      content.socialShareTitle = socialShareTitle
      content.socialShareDescription = socialShareDescription
      break
    }
    case 'learnMore': {
      content.title = t('memories.learnmore.page-title', 'Essential Features of FamilySearch Memories')
      content.description = t(
        'memories.learnmore.meta-description',
        'Bring your family photos, stories, and documents to life with these lesser-known, yet powerful, features of FamilySearch Memories.'
      )
      content.socialShareTitle = t('memories.learnmore.social.title', 'Have you tried FamilySearch Memories?')
      content.socialShareDescription = t(
        'memories.learnmore.social.description',
        'Save photos for generations come with these simple features.'
      )
      break
    }
    case 'people': {
      content.title = t('memories.people.meta.title', 'Photos and Stories')
      content.description = t(
        'memories.people.meta.description',
        'Discover your family history. Explore the world’s largest collection of free family trees, genealogy records and resources.'
      )
      content.socialShareTitle = t('memories.people.meta.title', 'Photos and Stories')
      content.socialShareDescription = t(
        'memories.people.meta.description',
        'Discover your family history. Explore the world’s largest collection of free family trees, genealogy records and resources.'
      )
      break
    }
    case 'restore': {
      content.title = t('memories.restore.heading', 'Memories Restoration Tool')
      content.description = ''
      break
    }
    case 'memory': {
      const dynamicTitle = dynamicMetadata?.title || dynamicMetadata?.datePlace
      content.title = `${dynamicTitle ? `${dynamicTitle} | ` : ''}${t('memories.viewer.default-page-title', 'Memories on FamilySearch')}`
      content.description = dynamicMetadata?.description || '' // Falls back to page default
      content.image.url = dynamicMetadata?.ogImage || learnMoreImage
      break
    }
    default:
  }

  return content
}

const MemoriesPageHead = ({ page, dynamicMetadata, containsAlbumUrl }) => {
  const { structuredData, ...metaContent } = useMetaContent(page, dynamicMetadata, containsAlbumUrl)

  return (
    <>
      <PageHead {...metaContent} includeHreflang />
      <Head>
        <meta name="apple-itunes-app" content="app-id=885970971" />
        {structuredData}
      </Head>
    </>
  )
}

export default MemoriesPageHead
